import { router } from '@inertiajs/react'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import ProjectDeliverySummaryRow from '@/frontend/features/Fde/ProjectDeliverySummaryRow'
import { fdeProjectPath } from '@/frontend/features/Fde/routes'
import type { ProjectDelivery, ProjectPlan } from '@/frontend/features/Fde/projectPlanTypes'
import type { FdeCopy, ProjectTask } from '@/frontend/features/Fde/types'

interface Props {
  plan: ProjectPlan
  copy: FdeCopy
  tasks: ProjectTask[]
  projectId: string
  today: string
  canManage: boolean
  onEdit: (delivery?: ProjectDelivery) => void
}

export default function ProjectPlanDeliveries(props: Props) {
  const { plan, tasks, projectId, copy } = props
  const words = copy.project_plan
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const active = plan.deliveries.filter(
    (delivery) => !['accepted', 'cancelled'].includes(delivery.status),
  )
  const history = plan.deliveries.filter((delivery) =>
    ['accepted', 'cancelled'].includes(delivery.status),
  )
  const accepted = plan.deliveries.filter((delivery) => delivery.status === 'accepted').length
  const total = plan.deliveries.filter((delivery) => delivery.status !== 'cancelled').length
  function accept(delivery: ProjectDelivery) {
    setBusy(true)
    setError('')
    router.patch(
      fdeProjectPath('/dashboard/discovery/project_plan', projectId),
      {
        project_plan: {
          lock_version: plan.lock_version,
          delivery: { id: delivery.id, status: 'accepted' },
        },
      },
      {
        preserveScroll: true,
        onError: (errors) => setError(Object.values(errors).join(' ')),
        onFinish: () => setBusy(false),
      },
    )
  }
  function row(delivery: ProjectDelivery) {
    return <ProjectDeliverySummaryRow key={delivery.id} delivery={delivery} tasks={tasks}
      copy={copy} projectId={projectId} today={props.today} canManage={props.canManage}
      busy={busy} onEdit={() => props.onEdit(delivery)} onAccept={() => accept(delivery)} />
  }
  return (
    <section aria-label={words.deliveries} className="min-w-0">
      <div className="mb-3 flex min-h-7 items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">
          {words.deliveries}
          {total > 0 && (
            <span className="ml-3 text-[11px] font-normal text-muted-foreground">
              {words.delivery_count
                .replace('%{accepted}', String(accepted))
                .replace('%{total}', String(total))}
            </span>
          )}
        </h2>
        {props.canManage && plan.deliveries.length < 12 && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => props.onEdit()}
          >
            <Plus className="size-3" />
            {words.add_delivery}
          </Button>
        )}
      </div>
      {active.length ? (
        <div className="divide-y divide-border rounded-lg border border-border bg-background">
          {active.map(row)}
        </div>
      ) : (
        <p className="rounded-lg border border-border bg-background px-5 py-6 text-xs text-muted-foreground">
          {total === accepted && total > 0 ? words.delivery_states.accepted : words.delivery_empty}
        </p>
      )}
      {history.length > 0 && (
        <details className="mt-3 rounded-lg border border-border bg-background">
          <summary className="cursor-pointer px-4 py-3 text-xs text-muted-foreground">
            {words.history} · {history.length}
          </summary>
          <div className="divide-y divide-border border-t border-border">{history.map(row)}</div>
        </details>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {error}
        </p>
      )}
    </section>
  )
}
